import React, { Fragment, useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Combobox, Transition } from "@headlessui/react";
import { debounce } from "lodash";
import { orderDeliveryService } from "../../services/orderDelivery.service";
import LogicContext from "../../Context/LogicContext";
import { taskStatusClass } from "./orderDeliveryStatus";

function OrderDeliverySearch() {
  const [searchResult, setSearchResult] = useState([]);
  const [textInput, setTextInput] = useState("");
  const navigate = useNavigate();
  const logicContext = useContext(LogicContext);

  const { pathname } = useLocation();
  var url = pathname.split("/");
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];

  const debouncedSearch = React.useRef(
    debounce((search: string) => {
      if (search.trim().length > 2) {
        orderDeliveryService.searchOrderDelivery(search.trim()).then((res) => {
          // Search response shape assumed to match the list (task_list)
          const rows = res?.data?.task_list ?? (Array.isArray(res?.data) ? res.data : []);
          if (rows.length) {
            setSearchResult(rows);
          } else {
            logicContext.showToast("No available record for your search query!", "");
            setSearchResult([]);
          }
        });
      }
    }, 500)
  ).current;

  function selectedOption(selection: any) {
    setSearchResult([]);
    setTextInput("");
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${selection?.sales_order_ref_no}`);
  }

  return (
    <>
      <Combobox>
        <div className="relative max-w-sm w-full">
          <div className="relative">
            <Combobox.Input
              className=" w-full rounded-full border font-medium border-gray-300 bg-white py-1.5 pl-10 pr-4 text-sm text-gray-800 placeholder-gray-400 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
              placeholder="Search in Order Delivery"
              onChange={(e) => {
                const val = e.target.value;
                setTextInput(val);
                debouncedSearch(val);
              }}
            />
            <Combobox.Button className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-5 text-gray-500"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                />
              </svg>
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setTextInput("")}
          >
            <Combobox.Options className="absolute z-50 mt-2 w-[440px] max-w-[90vw] overflow-hidden rounded-lg bg-white text-sm shadow-xl ring-1 ring-black/10 focus:outline-none">
              {searchResult.length === 0 && textInput !== "" ? (
                <div className="px-4 py-6 text-center text-sm text-gray-500">Nothing found.</div>
              ) : (
                <>
                  {searchResult.length > 0 && (
                    <div className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      {searchResult.length} order{searchResult.length === 1 ? "" : "s"} found
                    </div>
                  )}
                  <div className="max-h-80 overflow-y-auto">
                    {searchResult.map((row: any) => (
                      <Combobox.Option
                        key={row?.sales_order_ref_no}
                        onClick={() => selectedOption(row)}
                        className={({ active }) =>
                          `flex cursor-pointer select-none items-center gap-3 border-b border-gray-50 px-4 py-2.5 last:border-0 ${
                            active ? "bg-blue-50" : "bg-white"
                          }`
                        }
                        value={row}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="truncate text-sm font-semibold text-gray-800">
                              {row?.order_recipient_name?.trim() || "Unknown recipient"}
                            </p>
                            {row?.task_status_name && (
                              <span
                                className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                  (taskStatusClass as any)[row?.task_status_id] ?? "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {row.task_status_name}
                              </span>
                            )}
                          </div>
                          <p className="truncate text-xs text-gray-500">
                            {[row?.order_recipient_phone_number, row?.total_sales_order_amount].filter(Boolean).join(" · ") ||
                              "No contact details"}
                          </p>
                        </div>
                        <span className="flex-shrink-0 rounded bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                          #{row?.sales_order_ref_no}
                        </span>
                      </Combobox.Option>
                    ))}
                  </div>
                </>
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </>
  );
}

export default OrderDeliverySearch;
